import GlobalStyles from '@mui/material/GlobalStyles';
import { SnackbarProvider } from 'notistack';
import * as React from 'react';

import Button, { type ButtonProps } from '@/components/Button';
import Modal from '@/components/Modal';
import { SnackbarAlert } from '@/components/SnackbarAlert/SnackbarAlert';

interface SnackbarAlertProps {
  children?: React.ReactNode;
}

interface FeedbackModalState {
  title: string;
  description?: React.ReactNode;
  action?: ButtonProps;
  secondaryAction?: ButtonProps;
  actionArea?: React.ReactNode;
  onClose?: () => void;
  showClose?: boolean;
}

interface FeedbackModalContextType {
  openFeedback: (props: FeedbackModalState) => void;
  closeFeedback: () => void;
}

const FeedbackModalContext = React.createContext<FeedbackModalContextType | undefined>(undefined);

export const useFeedbackModal = (): FeedbackModalContextType => {
  const context = React.useContext(FeedbackModalContext);
  if (!context) {
    throw new Error('useFeedbackModal must be used within a FeedbackModalProvider');
  }
  return context;
};

const SnackbarAlertProvider: React.FC<SnackbarAlertProps> = ({ children }) => {
  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      autoHideDuration={null}
      disableWindowBlurListener
      TransitionProps={{ direction: 'up' }}
      Components={{
        success: SnackbarAlert,
        error: SnackbarAlert,
        warning: SnackbarAlert,
        info: SnackbarAlert,
        default: SnackbarAlert,
      }}
    >
      <GlobalStyles
        styles={{
          '.notistack-SnackbarContainer': {
            alignItems: 'center',
            flexDirection: 'column-reverse',
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: 'none',
            left: 0,
            right: 0,
            transform: 'none',
            paddingInline: 16,
            // Above the mobile dock while it shows (`--bp-bottom-dock`, set by MobileDock, already
            // counts the safe area).
            bottom:
              'calc(24px + max(var(--bp-bottom-dock, 0px), env(safe-area-inset-bottom, 0px)))',
          },
          '.notistack-Snackbar': {
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            minWidth: 0,
          },
          '.notistack-Snackbar > div': {
            display: 'flex',
            justifyContent: 'center',
            flexGrow: 0,
            width: '100%',
            minWidth: 0,
          },
        }}
      />

      {children}
    </SnackbarProvider>
  );
};

const FeedbackModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = React.useState(false);
  const [modalState, setModalState] = React.useState<FeedbackModalState>({
    title: '',
    description: '',
    actionArea: null,
  });

  const openFeedback = React.useCallback((props: FeedbackModalState) => {
    setModalState(props);
    setOpen(true);
  }, []);

  const closeFeedback = React.useCallback(() => {
    setOpen(false);
  }, []);

  const handleClose = React.useCallback(() => {
    modalState?.onClose?.();
    closeFeedback();
  }, [closeFeedback, modalState]);

  const dialog = React.useMemo(
    () => (
      <Modal
        open={open}
        maxWidth="xs"
        onDismiss={handleClose}
        title={modalState.title}
        showClose={modalState.showClose}
        actions={
          modalState.actionArea || modalState.action || modalState.secondaryAction ? (
            <>
              {modalState.actionArea}
              {modalState.secondaryAction ? (
                <Button
                  fullWidth
                  variant="outlined"
                  {...modalState.secondaryAction}
                  loading={modalState.secondaryAction.loading ?? false}
                />
              ) : null}
              {modalState.action ? (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  {...modalState.action}
                  loading={modalState.action.loading ?? false}
                />
              ) : null}
            </>
          ) : undefined
        }
      >
        {modalState.description}
      </Modal>
    ),
    [
      open,
      handleClose,
      modalState.title,
      modalState.description,
      modalState.actionArea,
      modalState.action,
      modalState.secondaryAction,
      modalState.showClose,
    ],
  );

  return (
    <FeedbackModalContext.Provider value={{ openFeedback, closeFeedback }}>
      {children}
      {dialog}
    </FeedbackModalContext.Provider>
  );
};

export const FeedbackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <SnackbarAlertProvider>
      <FeedbackModalProvider>{children}</FeedbackModalProvider>
    </SnackbarAlertProvider>
  );
};
